/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {useIntl, FormattedMessage, defineMessages} from 'react-intl'
import {useMultiStyleConfig, VisuallyHidden} from '@chakra-ui/react'
import {
    Flex,
    Heading,
    Button,
    Skeleton,
    Box,
    Text,
    VStack,
    Fade,
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {WishlistFilledIcon, WishlistIcon} from '../custom-icons'
import {useCurrency, useDerivedProduct} from '../../hooks'
import {useAddToCartModalContext} from './../../hooks/use-add-to-cart-modal'

// Constants
import {COLOR_ID_SEPARATOR} from '@app/util/constants'

// project components
import ImageGallery from '../image-gallery'
import Breadcrumb from '../breadcrumb'
import Link from '@salesforce/retail-react-app/app/components/link'
import withRegistration from '../with-registration'
import {Skeleton as ImageGallerySkeleton} from '@salesforce/retail-react-app/app/components/image-gallery'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import QuantityPicker from '../../components/quantity-picker'
import {useToast} from '@app/hooks/use-toast'
import {API_ERROR_MESSAGE, COLOR_ATTRIBUTE_ID} from '@app/constants'
import DisplayPrice from '../display-price'
import Swatch from '../swatch-group/swatch'
import SwatchGroup from '../swatch-group'
import ProductDetailsDrawer from './ProductDetailsDrawer'
import SizeGuideTrigger from './size-guide-trigger'
import PromoCallout from '../product-tile/promo-callout'

import {getPriceData, isAGECProduct} from '../../utils/product-utils'
// Utils
import {useWishlistActions} from '@app/hooks/use-wishlist-actions'
import {getProductType} from '@app/utils/product-utils'

// Hooks
import {useManualLinking} from '../../hooks/use-manual-linking'
import {useAutomaticLinking} from '../../hooks/use-automatic-linking'
import {useGTM} from '@app/hooks/use-gtm'

// Custom Context
import {useConversionRate} from '../../custom-contexts'

//utils
import debounce from 'lodash/debounce'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {Component, PageContext} from '@salesforce/commerce-sdk-react/components'
import {PDPPersonalizePush} from './personalizePush'

/**
 * Component: ProductViewHeader
 *
 * This functional component renders the header section of a product view, displaying the product's name,
 * price, and an optional promotional callout. It also includes a wishlist icon for user interaction.
 *
 * Props:
 * @param {Object} props - The properties object.
 * @param {string} props.name - The name of the product to be displayed in the header.
 * @param {string} props.currency - The currency code for displaying the product's price.
 * @param {Object} props.priceData - An object containing pricing information for the product.
 * @param {number} props.priceData.currentPrice - The current price of the product.
 * @param {Object} props.product - The product object containing detailed information about the product.
 * @param {boolean} props.isProductPartOfBundle - A flag indicating whether the product is part of a bundle.
 * @param {Object} props.styles - An object containing style properties for customizing the component's appearance.
 *
 * Returns:
 * @returns {JSX.Element} - A JSX element representing the product view header.
 *
 * Usage:
 * This component is used within a product detail page to provide a consistent and styled header section
 * that includes the product's name, price, and promotional details. It is designed to be flexible with
 * styling and can be customized via the `styles` prop.
 */
const ProductViewHeader = ({name, currency, priceData, product, isProductPartOfBundle, styles}) => {
    const {wishlist, addProductToWishlist, removeProductFromWishlist} = useWishlistActions()
    const {data: customer} = useCurrentCustomer()
    const intl = useIntl()

    const productId = product?.id

    const [isFavorite, setIsFavorite] = useState(false)

    const productType = getProductType(product)

    useEffect(() => {
        setIsFavorite(
            wishlist?.customerProductListItems?.some((item) => item.productId === productId)
        )
    }, [wishlist, productId])

    const handleWishlistToggle = async () => {
        if (isFavorite) {
            await removeProductFromWishlist(product)
            setIsFavorite(false)
        } else {
            await addProductToWishlist(product)
            setIsFavorite(true)
        }
    }

    // Determine the aria-label based on customer status and wishlist status
    const getAriaLabel = () => {
        if (isFavorite) {
            return intl.formatMessage({
                id: 'product_view.button.remove_favorite',
                defaultMessage: 'remove the product from your favorites'
            })
        } else if (customer.isRegistered) {
            return intl.formatMessage({
                id: 'product_view.button.add_favorite_customer_registered',
                defaultMessage: 'add the product to your favorites'
            })
        } else {
            return intl.formatMessage({
                id: 'product_view.button.add_favorite_guest',
                defaultMessage:
                    'add the product to your favorites. you must login or create an account to save your favorites'
            })
        }
    }
    return (
        <VStack {...styles.productViewHeaderContainer}>
            {/* Title */}
            <Skeleton isLoaded={name} {...styles.productBrandHeader}>
                <Heading as="div" {...styles.productViewHeaderTitle}>
                    {name}
                </Heading>
                {/* Bundles and Sets cannot be wishlisted */}
                {!productType.isSet && !productType.isBundle && (
                    <ButtonWithRegistration
                        onClick={handleWishlistToggle}
                        p={0}
                        minWidth={0}
                        {...styles.addToWishListInTitle}
                        aria-label={getAriaLabel()}
                    >
                        {isFavorite ? (
                            <WishlistFilledIcon
                                boxSize="12px"
                                svgTitle={intl.formatMessage({
                                    defaultMessage: 'remove from wishlist',
                                    id: 'product_view.icon.title.remove'
                                })}
                                svgDescription={intl.formatMessage({
                                    defaultMessage: 'Remove from wishlist icon',
                                    id: 'product_view.icon.description.remove'
                                })}
                            />
                        ) : (
                            <WishlistIcon
                                boxSize="12px"
                                svgTitle={intl.formatMessage({
                                    defaultMessage: 'add to wishlist',
                                    id: 'product_view.icon.title.add'
                                })}
                                svgDescription={intl.formatMessage({
                                    defaultMessage: 'Add to wishlist icon',
                                    id: 'product_view.icon.description.add'
                                })}
                            />
                        )}
                    </ButtonWithRegistration>
                )}
            </Skeleton>

            {!isProductPartOfBundle && (
                <Skeleton isLoaded={priceData?.currentPrice}>
                    <Box {...styles.productViewHeaderPrice}>
                        {!!priceData?.currentPrice && (
                            <DisplayPrice
                                labelForA11y={product?.name}
                                priceData={priceData}
                                currency={currency}
                                promoCallout={
                                    <Skeleton isLoaded={product}>
                                        <PromoCallout priceData={priceData} product={product} />
                                    </Skeleton>
                                }
                                {...styles.productPrice}
                            />
                        )}
                    </Box>
                </Skeleton>
            )}
        </VStack>
    )
}

ProductViewHeader.propTypes = {
    name: PropTypes.string,
    currency: PropTypes.string,
    category: PropTypes.array,
    priceData: PropTypes.object,
    product: PropTypes.object,
    isProductPartOfBundle: PropTypes.bool,
    styles: PropTypes.object
}

const ButtonWithRegistration = withRegistration(Button)

/**
 * Render a product detail view that includes name, image gallery, price,
 * variant selections, action buttons
 */

const ProductView = forwardRef(
    (
        {
            setErrorInventoryMessage,
            errorInventoryMessage,
            product,
            onProductIdChange,
            category,
            showFullLink = false,
            imageSize = 'md',
            isWishlistLoading = false,
            addToCart,
            updateCart,
            addToWishlist,
            updateWishlist,
            isProductLoading,
            isProductPartOfSet = false,
            isProductPartOfBundle = false,
            childOfBundleQuantity = 0,
            childProductOrderability,
            setChildProductOrderability,
            isBasketLoading = false,
            onVariantSelected = () => {},
            validateOrderability = (variant, quantity, stockLevel) =>
                !isProductLoading && variant?.orderable && quantity > 0 && quantity <= stockLevel,
            showImageGallery = true,
            setSelectedBundleQuantity = () => {},
            selectedBundleParentQuantity = 1,
            isModal = false,
            useUpdateLayout = Boolean(updateCart),
            shouldHideBreadcrumb = false,
            personalizePushComponent
        },
        ref
    ) => {
        const productType = getProductType(product)

        const {currency: activeCurrency} = useCurrency()
        const showToast = useToast()
        const intl = useIntl()
        const location = useLocation()
        const {
            isOpen: isAddToCartModalOpen,
            onOpen: onAddToCartModalOpen,
            onClose: onAddToCartModalClose
        } = useAddToCartModalContext()
        const theme = useTheme()
        const conversionsRate = useConversionRate()
        const {triggerViewItemEvent} = useGTM()
        const [showOptionsMessage, toggleShowOptionsMessage] = useState(false)
        const {
            showLoading,
            inventoryMessage,
            quantity,
            minOrderQuantity,
            setQuantity,
            variant,
            variationParams,
            variationAttributes,
            stockLevel,
            stepQuantity,
            isOutOfStock,
            unfulfillable
        } = useDerivedProduct(
            product,
            isProductPartOfSet,
            isProductPartOfBundle,
            errorInventoryMessage
        )

        const [showInventoryMessage, setShowInventoryMessage] = useState(false)

        // Personalize Push
        const PAGEDESIGNER_TO_COMPONENT = {
            'commerce_layouts.pdcPdpPersonalizePush': PDPPersonalizePush
        }
        const [contextValue] = useState({components: PAGEDESIGNER_TO_COMPONENT})

        const getVariationId = (id, value) => {
            /**
             * Construct an object with the selected attributes, by merging the selected variation attributes with the new selection
             * @example {color: '129', size: '2ANS'}
             */
            const selectedAttributes = variationAttributes.reduce((acc, attribute) => {
                return {
                    ...acc,
                    [attribute.id]: attribute.id === id ? value : attribute.selectedValue.value
                }
            }, {})

            // Get the master id with color apended, used as a backup
            const masterColorProductId = `${product.master.masterId}${COLOR_ID_SEPARATOR}${selectedAttributes[COLOR_ATTRIBUTE_ID]}`

            // Try to find the variation product that matches all the variationAttributes values
            const variationProduct = product?.variants.find((variant) =>
                Object.entries(variant.variationValues).every(
                    ([key, value]) => selectedAttributes[key] === value
                )
            )

            return variationProduct?.productId ?? masterColorProductId
        }

        const priceData = useMemo(() => {
            return getPriceData(product, {quantity})
        }, [product, quantity])
        const styles = useMultiStyleConfig(isModal ? 'CartEditModal' : 'ProductViewContainer')
        const canAddToWishlist = !isProductLoading
        const errorContainerRef = useRef(null)
        const {disableButton, customInventoryMessage} = useMemo(() => {
            let shouldDisableButton = !product?.inventory?.orderable
            let currentInventoryMsg = ''
            if (
                !shouldDisableButton &&
                (productType.isSet || productType.isBundle) &&
                childProductOrderability
            ) {
                // if any of the children are not orderable, it will disable the add to cart button
                const unavailableChildProductKey = Object.keys(childProductOrderability).find(
                    (key) => {
                        return childProductOrderability[key].showInventoryMessage
                    }
                )
                shouldDisableButton = !!unavailableChildProductKey
                if (unavailableChildProductKey) {
                    const unavailableChildProduct =
                        childProductOrderability[unavailableChildProductKey]
                    if (unavailableChildProduct.unfulfillable) {
                        currentInventoryMsg = intl.formatMessage(
                            {
                                defaultMessage: 'Only {stockLevel} left for {productName}!',
                                id: 'use_product.message.inventory_remaining_for_product'
                            },
                            {
                                stockLevel: unavailableChildProduct.stockLevel,
                                productName: unavailableChildProduct.productName
                            }
                        )
                    }
                    if (unavailableChildProduct.isOutOfStock) {
                        currentInventoryMsg = intl.formatMessage(
                            {
                                defaultMessage: 'Out of stock for {productName}',
                                id: 'use_product.message.out_of_stock_for_product'
                            },
                            {productName: unavailableChildProduct.productName}
                        )
                    }
                }
            }
            return {disableButton: shouldDisableButton, customInventoryMessage: currentInventoryMsg}
        }, [showInventoryMessage, childProductOrderability, product])

        const [missingAttributes, setMissingAttributes] = useState([])

        const validateAndShowError = (opts = {}) => {
            const {scrollErrorIntoView = true} = opts
            // Validate that all attributes are selected before proceeding.
            const hasValidSelection = validateOrderability(variant, quantity, stockLevel)
            const showError = !productType.isSet && !productType.isBundle && !hasValidSelection
            const scrollToError = showError && scrollErrorIntoView
            const missingAttributesList = []

            // check if Size or color attributes are missing
            variationAttributes.forEach(({id, selectedValue}) => {
                if (!selectedValue.name || !selectedValue.value) {
                    missingAttributesList.push(id) // Use ID to identify missing attribute
                }
            })

            setMissingAttributes(missingAttributesList)

            toggleShowOptionsMessage(showError)

            if (scrollToError && errorContainerRef.current) {
                errorContainerRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                })
            }

            return {hasValidSelection, missingAttributes}
        }

        const renderActionButtons = (additionalStyles = {}) => {
            const buttons = []
            const buttonText = {
                update: intl.formatMessage({
                    defaultMessage: 'Update',
                    id: 'product_view.button.update'
                }),
                addToCart: intl.formatMessage({
                    defaultMessage: 'ADD TO CART',
                    id: 'product_view.button.add_to_cart'
                }),
                addSetToCart: intl.formatMessage({
                    defaultMessage: 'Add Set to Cart',
                    id: 'product_view.button.add_set_to_cart'
                }),
                addBundleToCart: intl.formatMessage({
                    defaultMessage: 'Add Bundle to Cart',
                    id: 'product_view.button.add_bundle_to_cart'
                }),
                addToWishlist: intl.formatMessage({
                    defaultMessage: 'Add to Wishlist',
                    id: 'product_view.button.add_to_wishlist'
                }),
                addSetToWishlist: intl.formatMessage({
                    defaultMessage: 'Add Set to Wishlist',
                    id: 'product_view.button.add_set_to_wishlist'
                }),
                addBundleToWishlist: intl.formatMessage({
                    defaultMessage: 'Add Bundle to Wishlist',
                    id: 'product_view.button.add_bundle_to_wishlist'
                })
            }

            const showError = () => {
                showToast({
                    title: intl.formatMessage(API_ERROR_MESSAGE),
                    status: 'error'
                })
            }

            const handleCartItem = async () => {
                const {hasValidSelection} = validateAndShowError()

                if (isOutOfStock || unfulfillable) {
                    setShowInventoryMessage(true)
                    return
                } else {
                    setShowInventoryMessage(false)
                }

                if (!hasValidSelection) {
                    return null
                }

                if (!addToCart && !updateCart) return null
                if (updateCart) {
                    await updateCart(variant || product, quantity)
                    return
                }
                try {
                    const itemsAdded = await addToCart(variant, quantity)

                    // Open modal only when `addToCart` returns some data
                    // It's possible that the item has been added to cart, but we don't want to open the modal.
                    // See wishlist_primary_action for example.
                    if (itemsAdded) {
                        onAddToCartModalOpen({
                            product,
                            itemsAdded,
                            selectedQuantity: quantity
                        })
                    }
                } catch (e) {
                    showError()
                }
            }

            const handleWishlistItem = async () => {
                if (!updateWishlist && !addToWishlist) return null
                if (updateWishlist) {
                    updateWishlist(product, variant, quantity)
                    return
                }
                addToWishlist(product, variant, quantity)
            }

            // child product of bundles do not have add to cart button
            if ((addToCart || updateCart) && !isProductPartOfBundle) {
                buttons.push(
                    <Button
                        {...additionalStyles}
                        key="cart-button"
                        onClick={handleCartItem}
                        isDisabled={disableButton}
                        isLoading={isBasketLoading}
                        variant="primary"
                    >
                        {updateCart
                            ? buttonText.update
                            : productType.isSet
                            ? buttonText.addSetToCart
                            : productType.isBundle
                            ? buttonText.addBundleToCart
                            : buttonText.addToCart}
                    </Button>
                )
            }

            // child product of bundles do not have add to wishlist button
            if ((addToWishlist || updateWishlist) && !isProductPartOfBundle) {
                buttons.push(
                    <ButtonWithRegistration
                        key="wishlist-button"
                        onClick={handleWishlistItem}
                        disabled={isWishlistLoading || !canAddToWishlist}
                        isLoading={isWishlistLoading}
                        width="100%"
                        variant="secondary"
                    >
                        {updateWishlist
                            ? buttonText.update
                            : productType.isSet
                            ? buttonText.addSetToWishlist
                            : productType.isBundle
                            ? buttonText.addBundleToWishlist
                            : buttonText.addToWishlist}
                    </ButtonWithRegistration>
                )
            }

            return buttons
        }

        useEffect(() => {
            // Update the message if an availibility quantity is set
            if (quantity <= stockLevel) {
                setShowInventoryMessage(false)
            }
        }, [quantity, stockLevel])

        // Bind the reference with our `scope` that includes the internal validate function for this component.
        // Other values can be added to this scope as required.
        if (typeof ref === 'function') {
            ref = ref.bind({validateOrderability: validateAndShowError})
        }

        // Set the quantity of bundle child in a product bundle to ensure availability messages appear
        if (
            isProductPartOfBundle &&
            quantity != selectedBundleParentQuantity * childOfBundleQuantity
        ) {
            setQuantity(selectedBundleParentQuantity * childOfBundleQuantity)
        }

        useEffect(() => {
            if (isAddToCartModalOpen) {
                onAddToCartModalClose()
            }
        }, [location.pathname])

        useEffect(() => {
            if (
                !productType.isSet &&
                !productType.isBundle &&
                validateOrderability(variant, quantity, stockLevel)
            ) {
                toggleShowOptionsMessage(false)
            }
        }, [variationParams])

        useEffect(() => {
            if (variant) {
                onVariantSelected(product, variant, quantity)
            }
        }, [variant?.productId, quantity])

        useEffect(() => {
            if (isProductPartOfBundle || isProductPartOfSet) {
                const key = product.itemId ?? product.id
                // when showInventoryMessage is true, it means child product is not orderable
                setChildProductOrderability((previousState) => ({
                    ...previousState,
                    [key]: {
                        showInventoryMessage,
                        isOutOfStock,
                        unfulfillable,
                        stockLevel,
                        productName: product?.name
                    }
                }))
            }
        }, [showInventoryMessage, inventoryMessage])

        const [drawerOpened, setDrawerOpened] = useState(false)
        const [drawerContent, setDrawerContent] = useState('')

        /**
         * Opens the drawer with the specified content.
         *
         * @param {string} content - The content type to display in the drawer.
         */
        const openDrawer = (content) => {
            setDrawerContent(content)
            setDrawerOpened(true)
        }

        /**
         * Closes the drawer and resets the content.
         */
        const closeDrawer = () => {
            setDrawerOpened(false)
            setDrawerContent('')
        }

        /**
         * Static definition of messages for the drawer buttons using react-intl.
         */
        const drawersDetailsMessages = defineMessages({
            details: {
                id: 'product_view.pdp.drawer_details',
                defaultMessage: 'Details'
            },
            sustainability: {
                id: 'product_view.pdp.drawer_sustainability',
                defaultMessage: 'Traceability'
            },
            care: {
                id: 'product_view.pdp.drawer_care',
                defaultMessage: 'Care'
            },
            shipping: {
                id: 'product_view.pdp.drawer_shipping',
                defaultMessage: 'Shipping'
            }
        })

        /**
         * Array of objects representing the drawer buttons and their associated content.
         * @type {Array<{messageKey: string, content: string}>}
         */
        const buttonsDetailsDrawer = [
            {messageKey: 'details', content: 'details'},
            {messageKey: 'sustainability', content: 'sustainability'},
            {messageKey: 'care', content: 'care'},
            {messageKey: 'shipping', content: 'shipping'}
        ]

        /**
         * Renders buttons that open the drawer with specific content.
         * Filters out the sustainability button if the product is not a variant or not an AGEC product.
         *
         * @param {Object} additionalStyles - Additional styles to apply to the buttons.
         * @returns {JSX.Element[]} An array of button elements.
         *
         * @example
         * // Renders buttons for details, care, and shipping, but only renders the sustainability button
         * // if the product is a variant and an AGEC product.
         * Usage example: renderDrawerDetailsButtons({margin: '10px'});
         */
        const renderDrawerDetailsButtons = (additionalStyles = {}) => {
            return buttonsDetailsDrawer
                .filter(({content}) => {
                    if (content === 'sustainability') {
                        // Display the sustainability button only if the product is a variant and an AGEC product
                        return isAGECProduct(product)
                    }
                    return true
                })
                .map(({messageKey, content}) => (
                    <Button
                        key={content}
                        variant="unstyled"
                        onClick={() => openDrawer(content)}
                        cursor={'pointer'}
                        {...additionalStyles}
                    >
                        <FormattedMessage {...drawersDetailsMessages[messageKey]} />
                    </Button>
                ))
        }

        // Adjust mobile fixed display of product descriptions

        /**
         * A reference to the ProductViewHeader DOM element.
         * Used to directly access the element and measure its height.
         * @type {React.MutableRefObject<null>}
         */
        const ProductViewHeaderRef = useRef(null)
        /**
         * State to store the height of the ProductViewHeader.
         * Initialized to 0 and updated after the component mounts.
         * @type {number}
         */
        const [ProductViewHeaderHeight, setProductViewHeaderHeight] = useState(0)

        useEffect(() => {
            /**
             * Updates the height of the ProductViewHeader.
             * Checks if the reference is defined and updates the state with the current height of the element.
             */
            if (ProductViewHeaderRef.current) {
                setProductViewHeaderHeight(ProductViewHeaderRef.current.offsetHeight)
            }
        }, [product])

        useEffect(() => {
            /**
             * Applies a bottom margin to the footer equivalent to the height of the ProductViewHeader.
             * This function is called on mount and when the window is resized.
             */
            const applyMarginToFooter = () => {
                const footerElement = document.querySelector('#footer-main') // #footer-main => id of the footer element
                if (footerElement) {
                    footerElement.style.marginBottom = `${ProductViewHeaderHeight}px`
                }
            }

            // Debounce the function to limit how often it runs
            const debouncedApplyMarginToFooter = debounce(applyMarginToFooter, 200)

            // Apply the margin on mount
            applyMarginToFooter()

            // Add an event listener to apply the margin when the window is resized
            window.addEventListener('resize', debouncedApplyMarginToFooter)

            // Cleanup: Remove the event listener when the component unmounts
            return () => {
                window.removeEventListener('resize', debouncedApplyMarginToFooter)
                debouncedApplyMarginToFooter.cancel() // Cancel any pending debounced calls
            }
        }, [ProductViewHeaderHeight])

        //End Ajustement mobile fixed display of product descriptions

        const {manualLinkingResult, isManualLinkingEnabled} = useManualLinking(
            product,
            'c_perfectYourLook'
        )
        const {automaticLinkingResult, isAutomaticLinkingEnabled} = useAutomaticLinking(product)
        let perfectYourLookProducts
        if (isManualLinkingEnabled) {
            perfectYourLookProducts = manualLinkingResult
        } else if (isAutomaticLinkingEnabled) {
            perfectYourLookProducts = automaticLinkingResult
        } else {
            perfectYourLookProducts = []
        }

        /**
         * Handles the click event on a link and triggers a view item event.
         *
         * This function is responsible for handling the click event on a specific link
         * within the product view component. When the link is clicked, it triggers a
         * 'select_item' event using the `triggerViewItemEvent` function, passing the
         * current product variant as an argument. This is typically used for tracking
         * user interactions with product variants in analytics or other event-driven
         * systems.
         *
         * @function handleLinkClick
         * @returns {void} This function does not return a value.
         */
        const handleLinkClick = () => {
            triggerViewItemEvent('select_item', variant)
        }

        /**
         * useEffect hook to handle scrolling to the error message container when the
         * showOptionsMessage state changes to true. This ensures that the user is
         * directed to the relevant part of the page where they need to take action,
         * such as selecting a missing product attribute.
         *
         * The effect listens for changes to the `showOptionsMessage` state. When this
         * state becomes true, indicating that there are missing attributes that need
         * to be selected, the effect checks if the `errorContainerRef` is defined and
         * then scrolls the container into view smoothly.
         *
         * Dependencies:
         * - showOptionsMessage: Triggers the effect when its value changes.
         *
         * @function
         */
        useEffect(() => {
            if (showOptionsMessage && errorContainerRef.current) {
                errorContainerRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                })
            }
        }, [showOptionsMessage])

        /**
         * Extracts the available sizes for a specific color from a product's variants.
         *
         * This function filters the product's variants to find those that match the currently
         * selected color (`product.c_color`) and maps these filtered variants to extract the
         * available sizes for that color.
         *
         * @param {Object} product - The product object containing variant information.
         * @param {Array} product.variants - An array of variant objects, each representing a different
         *                                   variant of the product (e.g., different sizes, colors).
         * @param {string} product.c_color - The currently selected color value for the product.
         * @returns {Array<string>} An array of size strings that are available for the selected color.
         *
         * @example
         * // Assuming product.c_color is "010" and there are variants with sizes "S", "M", and "L"
         * // for this color, productAvailableSizes will be ["S", "M", "L"].
         */

        const productAvailableSizes = product?.variants
            ?.filter(
                (variant) =>
                    !product?.c_color || // master product won't have color assigned
                    variant?.variationValues?.color === product?.c_color
            )
            .map((variant) => variant?.variationValues?.size)

        return (
            <>
                {/* Breadcrumb at the top of the page */}
                {shouldHideBreadcrumb == false && product?.c_structuredDataCategories && (
                    <Skeleton isLoaded={product?.c_structuredDataCategories} minWidth={64}>
                        <Breadcrumb
                            categories={product?.c_structuredDataCategories}
                            product={product}
                            type="productBreadcrumb"
                        />
                    </Skeleton>
                )}
                {/* End Breadcrumb at the top of the page */}
                {/* Hidden H1 for accessibility using Chakra UI */}
                <VisuallyHidden as="h1">{product?.name}</VisuallyHidden>
                <Flex direction={'column'} data-testid="product-view" ref={ref}>
                    <Flex
                        direction={['column', 'column', 'column', 'row']}
                        {...styles.productViewLayout}
                    >
                        {/* Basic information etc. title, price, breadcrumb on mobile*/}
                        {useUpdateLayout && (
                            <Box display={['block', 'block', 'block', 'none']}>
                                <ProductViewHeader
                                    name={product?.name}
                                    product={product}
                                    priceData={priceData}
                                    currency={product?.currency || activeCurrency}
                                    category={category}
                                    isProductPartOfBundle={isProductPartOfBundle}
                                    styles={styles}
                                />
                            </Box>
                        )}
                        {showImageGallery && (
                            <Box {...styles.productImages}>
                                {product ? (
                                    <>
                                        <ImageGallery
                                            product={product}
                                            size={imageSize}
                                            imageGroups={product.imageGroups}
                                            selectedVariationAttributes={variationParams}
                                            lazy={isProductPartOfSet || isProductPartOfBundle}
                                            isCarouselOnMobile={!useUpdateLayout}
                                            isModal={isModal}
                                            perfectYourLookProducts={perfectYourLookProducts}
                                        />
                                        {personalizePushComponent && (
                                            <HideOnMobile>
                                                <PageContext.Provider value={contextValue}>
                                                    <Component
                                                        component={personalizePushComponent}
                                                    />
                                                </PageContext.Provider>
                                            </HideOnMobile>
                                        )}
                                        <HideOnMobile mt={16}>
                                            {showFullLink && product && (
                                                <Link
                                                    onClick={handleLinkClick}
                                                    to={`/product/${
                                                        updateCart
                                                            ? product?.id
                                                            : `${product?.master?.masterId}${COLOR_ID_SEPARATOR}${product?.variationValues[COLOR_ATTRIBUTE_ID]}`
                                                    }`}
                                                >
                                                    <FormattedMessage
                                                        id="product_view.link.full_details"
                                                        defaultMessage="See full details"
                                                    />
                                                </Link>
                                            )}
                                        </HideOnMobile>
                                    </>
                                ) : (
                                    <ImageGallerySkeleton />
                                )}
                            </Box>
                        )}
                        {/* Variations & Quantity Selector & CTA buttons */}
                        <Box {...styles.productDescription}>
                            <VStack {...styles.productAttributes}>
                                <Box display={['none', 'none', 'none', 'block']}>
                                    <ProductViewHeader
                                        name={product?.name}
                                        product={product}
                                        priceData={priceData}
                                        currency={product?.currency || activeCurrency}
                                        category={category}
                                        isProductPartOfBundle={isProductPartOfBundle}
                                        styles={styles}
                                    />
                                </Box>
                                <VStack
                                    align="stretch"
                                    spacing={useUpdateLayout ? [16, null, null, 32] : 4}
                                >
                                    {isProductPartOfBundle && (
                                        <Box>
                                            <Text variant={'bodySmall'} color="darkGray">
                                                <label>
                                                    {intl.formatMessage({
                                                        defaultMessage: 'Quantity:',
                                                        id: 'product_view.label.quantity'
                                                    })}
                                                    : {childOfBundleQuantity}
                                                </label>
                                            </Text>
                                        </Box>
                                    )}
                                    {/*
                                Customize the skeletons shown for attributes to suit your needs. At the point
                                that we show the skeleton we do not know how many variations are selectable. So choose
                                a a skeleton that will meet most of your needs.
                            */}
                                    {showLoading ? (
                                        <>
                                            {/* First Attribute Skeleton */}
                                            <Skeleton height={6} width={32} />
                                            <Skeleton height={20} width={64} />

                                            {/* Second Attribute Skeleton */}
                                            <Skeleton height={6} width={32} />
                                            <Skeleton height={20} width={64} />
                                        </>
                                    ) : (
                                        variationAttributes.map(
                                            ({id, name, selectedValue, values}) => {
                                                const filteredValues =
                                                    id === 'size'
                                                        ? values?.filter((value) =>
                                                              productAvailableSizes?.includes(
                                                                  value.value
                                                              )
                                                          )
                                                        : values
                                                const swatches = filteredValues?.map(
                                                    (
                                                        {href, name, image, value, orderable},
                                                        index
                                                    ) => {
                                                        const content = image ? (
                                                            <Box
                                                                backgroundColor={name.toLowerCase()}
                                                                backgroundImage={`url(${
                                                                    image?.disBaseLink ||
                                                                    image?.link
                                                                })`}
                                                                {...styles.colorSwatches}
                                                            />
                                                        ) : (
                                                            name
                                                        )
                                                        const hasSelection = Boolean(
                                                            selectedValue?.value
                                                        )
                                                        const isSelected =
                                                            selectedValue?.value === value
                                                        const isFirst = index === 0
                                                        // To mimic the behavior of a native radio input, only
                                                        // one swatch should receive tab focus; the rest can be
                                                        // selected using arrow keys when the swatch group has
                                                        // focus. The focused element is the selected option or
                                                        // the first in the group, if no option is selected.
                                                        // This is a slight difference, for simplicity, from the
                                                        // native element, where the first element is focused on
                                                        // `Tab` and the _last_ element is focused on `Shift+Tab`
                                                        const isFocusable =
                                                            isSelected || (!hasSelection && isFirst)

                                                        return (
                                                            <Swatch
                                                                key={value}
                                                                disabled={!orderable}
                                                                value={value}
                                                                href={
                                                                    onProductIdChange ? null : href
                                                                }
                                                                name={name}
                                                                variant={
                                                                    id === 'color'
                                                                        ? 'color'
                                                                        : 'size'
                                                                }
                                                                selected={isSelected}
                                                                isFocusable={isFocusable}
                                                            >
                                                                {content}
                                                            </Swatch>
                                                        )
                                                    }
                                                )
                                                return (
                                                    <Box {...styles.productSwatches} key={id}>
                                                        <SwatchGroup
                                                            variant={
                                                                id === 'color' ? 'color' : 'size'
                                                            }
                                                            value={selectedValue?.value}
                                                            displayName={selectedValue?.name || ''}
                                                            handleChange={(value) => {
                                                                onProductIdChange?.(
                                                                    getVariationId(id, value)
                                                                )
                                                            }}
                                                            label={intl.formatMessage(
                                                                {
                                                                    defaultMessage: '{variantType}',
                                                                    id: 'product_view.label.variant_type'
                                                                },
                                                                {variantType: name}
                                                            )}
                                                        >
                                                            {swatches}
                                                        </SwatchGroup>
                                                        {id === 'size' && !useUpdateLayout && (
                                                            <SizeGuideTrigger
                                                                position={'absolute'}
                                                                top={0}
                                                                right={0}
                                                                product={product}
                                                            />
                                                        )}
                                                    </Box>
                                                )
                                            }
                                        )
                                    )}

                                    {!productType.isSet && !isProductPartOfBundle && (
                                        <VStack align="stretch" maxWidth={'200px'}>
                                            <Text variant="bodySmall" color="darkGray">
                                                <label htmlFor="quantity">
                                                    {intl.formatMessage({
                                                        defaultMessage: 'Quantity:',
                                                        id: 'product_view.label.quantity'
                                                    })}
                                                </label>
                                            </Text>

                                            <QuantityPicker
                                                id="quantity"
                                                step={stepQuantity}
                                                value={quantity}
                                                min={minOrderQuantity}
                                                onChange={(stringValue, numberValue) => {
                                                    // Set the Quantity of product to value of input if value number
                                                    if (numberValue >= 0) {
                                                        setQuantity(numberValue)
                                                        setShowInventoryMessage(false)
                                                        setErrorInventoryMessage(false)
                                                        if (productType.isBundle)
                                                            setSelectedBundleQuantity(numberValue)
                                                    } else if (stringValue === '') {
                                                        // We want to allow the use to clear the input to start a new input so here we set the quantity to '' so NAN is not displayed
                                                        // User will not be able to add '' qauntity to the cart due to the add to cart button enablement rules
                                                        setQuantity(stringValue)
                                                    }
                                                }}
                                                onBlur={(e) => {
                                                    // Default to 1the `minOrderQuantity` if a user leaves the box with an invalid value
                                                    const value = e.target.value
                                                    if (parseInt(value) < 0 || value === '') {
                                                        setQuantity(minOrderQuantity)
                                                        if (productType.isBundle)
                                                            setSelectedBundleQuantity(
                                                                minOrderQuantity
                                                            )
                                                    }
                                                }}
                                                onFocus={(e) => {
                                                    // This is useful for mobile devices, this allows the user to pop open the keyboard and set the
                                                    // new quantity with one click. NOTE: This is something that can be refactored into the parent
                                                    // component, potentially as a prop called `selectInputOnFocus`.
                                                    e.target.select()
                                                }}
                                            />
                                            {!showLoading &&
                                                (showInventoryMessage || errorInventoryMessage) && (
                                                    <Text
                                                        variant="bodyLarge2"
                                                        color="red"
                                                        mt={2}
                                                        role="alert"
                                                    >
                                                        {inventoryMessage}
                                                    </Text>
                                                )}
                                        </VStack>
                                    )}
                                    {!showLoading && showOptionsMessage && (
                                        <Box ref={errorContainerRef}>
                                            <Fade in={true}>
                                                {missingAttributes.includes('color') && (
                                                    <Text
                                                        variant={'bodyLarge2'}
                                                        role="alert"
                                                        fontSize="sm"
                                                        color="red"
                                                    >
                                                        {intl.formatMessage({
                                                            defaultMessage: 'Please select a color',
                                                            id: 'product_view.error.missing_color'
                                                        })}
                                                    </Text>
                                                )}
                                                {missingAttributes.includes('size') && (
                                                    <Text
                                                        variant={'bodyLarge2'}
                                                        role="alert"
                                                        fontSize="sm"
                                                        color="red"
                                                    >
                                                        {intl.formatMessage({
                                                            defaultMessage: 'Please select a size',
                                                            id: 'product_view.error.missing_size'
                                                        })}
                                                    </Text>
                                                )}
                                            </Fade>
                                        </Box>
                                    )}
                                    <HideOnDesktop>
                                        {showFullLink && product && (
                                            <Link
                                                onClick={handleLinkClick}
                                                to={`/product/${
                                                    updateCart
                                                        ? product?.id
                                                        : product?.master?.masterId
                                                }`}
                                            >
                                                <FormattedMessage
                                                    id="product_view.link.full_details"
                                                    defaultMessage="See full details"
                                                />
                                            </Link>
                                        )}
                                    </HideOnDesktop>
                                    {productType.isSet && <p>{product?.shortDescription}</p>}
                                </VStack>

                                <Box>
                                    {!showLoading && customInventoryMessage && (
                                        <Fade in={true}>
                                            <Text variant="eyeBrowLarge" my={8}>
                                                {customInventoryMessage}
                                            </Text>
                                        </Fade>
                                    )}
                                    {conversionsRate?.isInternationalOrder && (
                                        <Box textAlign="center">
                                            <Text variant={'bodyLarge2'}>
                                                {intl.formatMessage({
                                                    id: 'zonos.dislaimer.general',
                                                    defaultMessage:
                                                        'Duties and taxes calculated at checkout.'
                                                })}
                                            </Text>
                                        </Box>
                                    )}
                                    <Box
                                        display={
                                            isProductPartOfSet
                                                ? 'block'
                                                : ['none', 'none', 'none', 'block']
                                        }
                                    >
                                        {renderActionButtons(styles.productViewPageButtonAddToCart)}
                                    </Box>
                                </Box>
                            </VStack>
                            {/* Desktop: Product details, sustainability, care, and shipping*/}
                            {!useUpdateLayout && (
                                <HideOnMobile>
                                    <Box {...styles.productDetailsDescriptionDesktop}>
                                        {renderDrawerDetailsButtons()}
                                    </Box>

                                    <ProductDetailsDrawer
                                        isOpen={drawerOpened}
                                        onClose={closeDrawer}
                                        currentContent={drawerContent}
                                        productDetails={product?.c_composition}
                                        shortDescription={product?.shortDescription}
                                        longDescription={product?.longDescription}
                                        productCare={product?.['c_careInstructions']}
                                        product={product}
                                    />
                                </HideOnMobile>
                            )}
                            {/*End Desktop: Product details, sustainability, care, and shipping */}
                        </Box>
                    </Flex>

                    {/* Sticky call-to-action buttons for mobile and certain product types */}
                    <Box
                        ref={ProductViewHeaderRef}
                        position="fixed"
                        bg="white"
                        width="100%"
                        display={
                            isProductPartOfSet || isProductPartOfBundle
                                ? 'none'
                                : ['block', 'block', 'block', 'none']
                        }
                        p={['8px 10px', null, 6]}
                        left={0}
                        bottom={0}
                        zIndex={2}
                        boxShadow={theme.shadows.top}
                        borderTopColor={'borderGray'}
                        borderTopWidth={useUpdateLayout ? 1 : 0}
                    >
                        {!useUpdateLayout && (
                            <ProductViewHeader
                                name={product?.name}
                                product={product}
                                priceData={priceData}
                                currency={product?.currency || activeCurrency}
                                category={category}
                                isProductPartOfBundle={isProductPartOfBundle}
                                styles={styles}
                            />
                        )}
                        {renderActionButtons(styles.productViewPageButtonAddToCart)}
                    </Box>
                </Flex>
            </>
        )
    }
)

ProductView.displayName = 'ProductView'

ProductView.propTypes = {
    product: PropTypes.object,
    /**
     * onProductIdChange is a callback called with the selected master/variation product id.
     * If not provided, the Swatches are rendered as links, if provided, they are rendered as buttons.
     */
    onProductIdChange: PropTypes.func,
    isProductPartOfSet: PropTypes.bool,
    isProductPartOfBundle: PropTypes.bool,
    childOfBundleQuantity: PropTypes.number,
    category: PropTypes.array,
    isProductLoading: PropTypes.bool,
    isBasketLoading: PropTypes.bool,
    isWishlistLoading: PropTypes.bool,
    addToCart: PropTypes.func,
    addToWishlist: PropTypes.func,
    updateCart: PropTypes.func,
    updateWishlist: PropTypes.func,
    showFullLink: PropTypes.bool,
    imageSize: PropTypes.oneOf(['sm', 'md']),
    childProductOrderability: PropTypes.object,
    setChildProductOrderability: PropTypes.func,
    onVariantSelected: PropTypes.func,
    validateOrderability: PropTypes.func,
    showImageGallery: PropTypes.bool,
    setSelectedBundleQuantity: PropTypes.func,
    selectedBundleParentQuantity: PropTypes.number,
    // This component can be used by the PDP and by the ProductViewModal
    isModal: PropTypes.bool,
    // Can be used to display the "update" layout without passing an update function, ex: wishlist page
    useUpdateLayout: PropTypes.bool,
    setErrorInventoryMessage: PropTypes.func,
    errorInventoryMessage: PropTypes.bool,
    shouldHideBreadcrumb: PropTypes.bool,
    personalizePushComponent: PropTypes.object
}

export default ProductView
